import React from 'react'
import {
  Box,
  InputBase,
  type InputBaseProps,
  InputLabel,
  type OutlinedInputProps,
  type SxProps,
  styled,
} from '@mui/material'

import { Icon, Typography } from '~components/internal'
import {
  borderRadius,
  inputShadows,
  lightTokens,
  spacing,
  typography as typographyTokens,
} from '~tokens/internal'

const TextFieldBase = styled(InputBase)(({ theme, error, disabled }) => ({
  width: '100%',
  borderRadius: borderRadius.sm,
  backgroundColor: disabled ? lightTokens.input.disabledBackground : lightTokens.input.background,
  boxShadow: disabled ? inputShadows.disabled : error ? inputShadows.error : inputShadows.default,
  transition: theme.transitions.create(['box-shadow']),

  '&:hover': !disabled && {
    boxShadow: error ? inputShadows.error : inputShadows.hover,
  },

  '&:focus-within': !disabled && {
    boxShadow: inputShadows.focus,
    outline: inputShadows.focusRing,
    outlineOffset: 0,
  },
  '&.MuiInputBase-adornedStart, &.MuiInputBase-adornedEnd': {
    display: 'flex',
    alignItems: 'center',
  },

  '& .MuiInputBase-input': {
    fontSize: typographyTokens.fontSize.sm,
    padding: `${spacing.xxs} ${spacing.compact}`,
    fontFamily: typographyTokens.fontFamily.body,
    border: 'none',
    backgroundColor: 'transparent',
    color: disabled ? theme.palette.text.secondary : theme.palette.text.primary,
    outline: 'none',
    width: '100%',
    height: '24px',
    '&::placeholder': {
      color: lightTokens.input.placeholder,
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
    maxHeight: 'none',
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: spacing.compact,
    marginRight: spacing.xxs,
  },
  '& .MuiInputAdornment-positionEnd': {
    marginLeft: spacing.xxs,
    marginRight: spacing.compact,
  },
  '&.MuiInputBase-adornedStart .MuiInputBase-input': {
    paddingLeft: spacing.micro,
    flex: 1,
    minWidth: 0,
  },
  '&.MuiInputBase-adornedEnd .MuiInputBase-input': {
    paddingRight: spacing.micro,
    flex: 1,
    minWidth: 0,
  },
}))

const TextFieldBorderless = styled(InputBase)(({ theme, disabled }) => ({
  width: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  outline: 'none',
  borderRadius: 0,

  '&:hover': {
    boxShadow: 'none',
  },

  '&:focus-within': {
    boxShadow: 'none',
    outline: 'none',
  },

  '& .MuiInputBase-input': {
    fontSize: typographyTokens.fontSize.sm,
    padding: '2px 0',
    fontFamily: typographyTokens.fontFamily.body,
    border: 'none',
    backgroundColor: 'transparent',
    color: disabled ? theme.palette.text.secondary : theme.palette.text.primary,
    outline: 'none',
    width: '100%',
    height: 'auto',
    '&::placeholder': {
      color: lightTokens.input.placeholder,
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
    maxHeight: 'none',
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: spacing.xxs,
  },
  '& .MuiInputAdornment-positionEnd': {
    marginLeft: spacing.xxs,
  },
  '&.MuiInputBase-adornedStart .MuiInputBase-input': {
    paddingLeft: 0,
    flex: 1,
    minWidth: 0,
  },
  '&.MuiInputBase-adornedEnd .MuiInputBase-input': {
    paddingRight: 0,
    flex: 1,
    minWidth: 0,
  },
}))

export const InputLabelStyled = styled(InputLabel)(({ theme }) => ({
  letterSpacing: typographyTokens.letterSpacing.normal,
  '&.Mui-focused': {
    color: theme.palette.text.primary,
  },
}))

export const TextFieldLabel: React.FC<{
  id?: string
  label: string
  disabled?: boolean
  required?: boolean
  InputLabelProps?: React.ComponentProps<typeof InputLabel>
}> = ({ id, label, required, disabled, InputLabelProps }) => {
  return (
    <InputLabelStyled
      htmlFor={id || 'text-field'}
      shrink
      sx={{
        color: disabled ? 'text.disabled' : 'text.secondary',
        '&.Mui-focused': {
          color: disabled ? 'text.secondary' : 'text.primary',
        },
      }}
      {...InputLabelProps}
    >
      <Typography variant='inherit' component='span' required={required}>
        {label}
      </Typography>
    </InputLabelStyled>
  )
}

export const ErrorText: React.FC<{ error: string }> = ({ error }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xxs,
        mt: spacing.xxs,
        p: spacing.compact,
        bgcolor: lightTokens.error.lighter,
        borderRadius: borderRadius.sm,
      }}
    >
      <Icon name='Error' size={20} color={lightTokens.error.main} />
      <Typography variant='body2' sx={{ color: lightTokens.error.dark }}>
        {error}
      </Typography>
    </Box>
  )
}

export const HelperText: React.FC<{ text: string }> = ({ text }) => {
  return (
    <Typography
      variant='body2'
      sx={{
        fontSize: typographyTokens.fontSize.xxs,
        fontFamily: typographyTokens.fontFamily.body,
        fontWeight: typographyTokens.fontWeight.regular,
        lineHeight: typographyTokens.lineHeight.normal,
        mt: spacing.xxs,
        color: 'text.secondary',
      }}
    >
      {text}
    </Typography>
  )
}

export interface TextFieldBaseProps extends Omit<InputBaseProps, 'error' | 'ref'> {
  id?: string
  variant?: 'outlined' | 'filled' | 'standard' | 'borderless' // Add borderless variant
  size?: 'small' | 'medium' // Add back size prop for compatibility
  InputProps?: Partial<OutlinedInputProps> // Add back InputProps for adornments and other features
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
}

export interface TextFieldContainerProps {
  label?: string
  required?: boolean
  helperText?: string
  error?: boolean
  errorText?: string
  containerStyle?: SxProps
  InputLabelProps?: React.ComponentProps<typeof InputLabel>
}

export interface TextFieldProps extends TextFieldBaseProps, TextFieldContainerProps {}

const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      label,
      required,
      id,
      error,
      errorText,
      helperText,
      disabled,
      multiline,
      rows,
      variant = 'outlined',
      size = 'medium', // eslint-disable-line
      containerStyle,
      InputProps,
      InputLabelProps,
      inputRef,
      ...props
    },
    ref,
  ) => {
    const inputPropsRef = InputProps?.ref
    const combinedRef = (node: HTMLDivElement | null): void => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(node)
        } else {
          ;(ref as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }
      if (inputPropsRef) {
        if (typeof inputPropsRef === 'function') {
          inputPropsRef(node)
        } else {
          ;(inputPropsRef as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }
      if (inputRef) {
        if (typeof inputRef === 'function') {
          inputRef(node as HTMLInputElement | HTMLTextAreaElement | null)
        } else {
          ;(
            inputRef as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
          ).current = node as HTMLInputElement | HTMLTextAreaElement | null
        }
      }
    }

    return (
      <Box sx={containerStyle}>
        {label && (
          <TextFieldLabel
            id={id}
            label={label}
            required={required}
            disabled={disabled}
            InputLabelProps={InputLabelProps}
          />
        )}
        {variant === 'borderless' ? (
          <TextFieldBorderless
            id={id || 'text-field'}
            error={error}
            disabled={disabled}
            multiline={multiline}
            rows={rows}
            ref={combinedRef}
            startAdornment={InputProps?.startAdornment}
            endAdornment={InputProps?.endAdornment}
            className={InputProps?.className}
            onMouseDown={InputProps?.onMouseDown}
            {...props}
            inputProps={props.inputProps}
          />
        ) : (
          <TextFieldBase
            id={id || 'text-field'}
            error={error}
            disabled={disabled}
            multiline={multiline}
            rows={rows}
            ref={combinedRef}
            startAdornment={InputProps?.startAdornment}
            endAdornment={InputProps?.endAdornment}
            className={InputProps?.className}
            onMouseDown={InputProps?.onMouseDown}
            {...props}
            inputProps={props.inputProps}
          />
        )}
        {error && errorText && <ErrorText error={errorText} />}
        {helperText && <HelperText text={helperText} />}
      </Box>
    )
  },
)

export default TextField
